/**
 * The particle orb: 720 points on a sphere (golden-angle spiral), turning, drawn on a canvas. Each
 * state moves them its own way — listening ripples, thinking pulses inwards, speaking flows and swirls,
 * connecting gathers them in a ring, error shakes — and the change from one to the next is a glide
 * (orb-state.ts). An emoji cue adds its own movement for a moment (reactions.ts).
 * From the "Particles Orb" component, ported to the kiosk.
 *
 * `size` is the canvas side in px; the sphere fills about two thirds of it, the rest is room to move.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useOrbAnimator, type OrbFrame } from './animator';
import { ERROR_FROM, ERROR_TO, mixRgb, resolveColor, rgba, type Rgb } from './orb-color';
import { blendStates, clamp01, type OrbState } from './orb-state';
import { STATES } from './params';
import { reactionAt, withReaction } from './reactions';
import type { FaceCue } from '../cues';

const PARTICLE_COUNT = 720;
const TWO_PI = Math.PI * 2;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const TIME_OFFSET = 1.7;
const TONE_BUCKETS = 6;
const ALPHA_BUCKETS = 10;
const ANGLE_X = 0.32;

/** A point of the sphere, and where it lands on screen this frame. */
interface Particle {
  index: number;
  x: number; y: number; z: number;
  ringFrac: number; seed: number; tone: number; toneBucket: number;
  sx: number; sy: number; r: number;
}

const SPHERE: readonly Particle[] = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
  const y = 1 - (i / (PARTICLE_COUNT - 1)) * 2;
  const radiusAtY = Math.sqrt(1 - y * y);
  const theta = GOLDEN_ANGLE * i;
  const tone = (i * 0.5436890126) % 1;
  return {
    index: i,
    x: Math.cos(theta) * radiusAtY, y, z: Math.sin(theta) * radiusAtY,
    ringFrac: (i * 0.61803398875) % 1,
    seed: ((i * 0.7548776662) % 1) * TWO_PI,
    tone,
    toneBucket: Math.min(TONE_BUCKETS - 1, Math.floor(tone * TONE_BUCKETS)),
    sx: 0, sy: 0, r: 0,
  };
});

export interface ParticlesOrbProps {
  state: OrbState;
  /** Canvas side, px. */
  size: number;
  /** Any CSS colour (a theme token works): the particles go from it, much lighter, to it a little lighter. */
  color?: string;
  /** Changes when the theme does, so `color` is read again. */
  paletteKey?: string;
  /** `glow`: light points that add up (dark backgrounds); `solid`: the brand's colour as it is (light ones). */
  look?: 'glow' | 'solid';
  speed?: number;
  /** The last emoji cue: the orb reacts to it. */
  cue?: FaceCue | null;
  /** 0..1 live audio amplitude; negative or absent: each state's own movement. */
  levelRef?: RefObject<number>;
  label?: string;
}

const WHITE: Rgb = [255, 255, 255];
/** A soft halo in the brand's colour around the points. */
const GLOW = 'drop-shadow(0 0 10px color-mix(in srgb, var(--color-accent) 55%, transparent))';
/** On a light or busy background, a faint shadow keeps the points apart from it. */
const SHADOW = 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.25))';
const FALLBACK: Rgb = [166, 22, 10];

export function ParticlesOrb({ state, size, color = 'var(--color-accent)', paletteKey, look = 'glow', cue = null, speed = 1, levelRef, label }: ParticlesOrbProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colorsRef = useRef({ from: WHITE, to: FALLBACK });
  const cueRef = useRef(cue);
  const drawRef = useRef<((frame: OrbFrame) => void) | null>(null);

  const onFrame = useCallback((frame: OrbFrame) => drawRef.current?.(frame), []);
  useOrbAnimator(hostRef, { state, levelRef, speed, onFrame });

  useLayoutEffect(() => {
    if (!hostRef.current) return;
    const accent = resolveColor(hostRef.current, color, FALLBACK);
    // glow: light enough to shine on a dark page ('lighter' blending adds them up); solid: the brand itself
    colorsRef.current = look === 'glow'
      ? { from: mixRgb(accent, WHITE, 0.72), to: mixRgb(accent, WHITE, 0.18) }
      : { from: mixRgb(accent, WHITE, 0.35), to: accent };
  }, [color, paletteKey, look]);
  const lookRef = useRef(look);
  useEffect(() => { lookRef.current = look; }, [look]);
  useEffect(() => { cueRef.current = cue; }, [cue]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || size <= 0) return; // no canvas (tests) or no room yet
    const canvas = ctx.canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;

    const center = size / 2;
    const smallness = Math.min(1, size / 140);
    // the points were sized for a ~170 px orb: on a larger one they grow too, or it looks sparse
    const dotScale = (0.55 + 0.45 * smallness) * Math.max(1, size / 200) ** 0.8;
    const alphaScale = smallness * smallness;
    const baseRadius = center * 0.62;
    const buckets: Particle[][] = Array.from({ length: TONE_BUCKETS * ALPHA_BUCKETS }, () => []);
    const toneStyles: string[] = new Array<string>(TONE_BUCKETS).fill('#fff');
    const cosX = Math.cos(ANGLE_X);
    const sinX = Math.sin(ANGLE_X);

    let paletteKey = '';
    let lastPhase = 0;
    let clock = 0;
    let pulseClock = 0;
    let angleY = 0;
    let ringPhase = 0;

    const ensurePalette = (errorWeight: number) => {
      const { from, to } = colorsRef.current;
      const e = Math.round(errorWeight * 64) / 64;
      const key = `${from.join()}|${to.join()}|${e}`;
      if (key === paletteKey) return;
      paletteKey = key;
      const f = mixRgb(from, ERROR_FROM, e);
      const t = mixRgb(to, ERROR_TO, e);
      for (let b = 0; b < TONE_BUCKETS; b += 1) toneStyles[b] = rgba(mixRgb(f, t, (b + 0.5) / TONE_BUCKETS), 1);
    };

    const draw = (frame: OrbFrame) => {
      const p = withReaction(blendStates(frame.weights, STATES), reactionAt(cueRef.current, Date.now()));
      const dPhase = Math.max(0, frame.phase - lastPhase);
      lastPhase = frame.phase;
      const level = frame.reduced ? p.rest : frame.level;

      clock += dPhase * p.tempo;
      pulseClock += dPhase * p.tempo * p.pulseRate;
      angleY += dPhase * p.spin * (1 + p.ripple * level * 1.8);
      ringPhase = (ringPhase + dPhase * 0.7) % TWO_PI;
      const t = clock + TIME_OFFSET;
      const pt = pulseClock + TIME_OFFSET;

      const beat = Math.sin(pt * 2.6) * 0.5 + 0.5;
      const radius = baseRadius * (1 + p.grow + p.breathe * Math.sin(t * 1.1) + level * p.swell - p.pulse * (0.06 + 0.12 * beat ** 3));
      const shakeAmp = p.shake * radius * 0.05;
      const shakeX = shakeAmp * (Math.sin(t * 26) + 0.5 * Math.sin(t * 15.7));
      const shakeY = shakeAmp * (Math.cos(t * 22.5) + 0.5 * Math.sin(t * 13.1));
      const driftAmp = p.drift * radius * 0.055;
      const jitterAmp = p.jitter * radius * (0.012 + level * 0.07);
      const rippleAmp = p.ripple * (0.04 + level * 0.22);
      const pulseAmp = p.pulse * 0.16 * (0.4 + 0.6 * beat);
      const flowAmp = p.flow * (0.18 + level * 0.4);
      const swirlAmp = p.swirl * (0.35 + level * 0.9);
      const ringW = clamp01(p.ring);
      const ringBreath = 1 + p.pulse * 0.4 * Math.sin(pt * 2.6);

      for (const bucket of buckets) bucket.length = 0;

      for (const q of SPHERE) {
        const twist = swirlAmp > 0.002 ? angleY + swirlAmp * Math.sin(q.y * 2.4 + t * 1.6) : angleY;
        const cy = Math.cos(twist);
        const sy = Math.sin(twist);
        const x1 = q.x * cy - q.z * sy;
        const z1 = q.x * sy + q.z * cy;
        const y1 = q.y * cosX - z1 * sinX;
        const depth = (q.y * sinX + z1 * cosX + 1) / 2;
        const perspective = 0.65 + depth * 0.45;

        let r = radius;
        if (rippleAmp > 0.002) r *= 1 + rippleAmp * (0.5 + 0.5 * Math.sin(q.y * 4.5 - t * 6.5));
        if (pulseAmp > 0.002) r *= 1 - pulseAmp * (0.5 + 0.5 * Math.sin(q.ringFrac * TWO_PI + pt * 3.1));
        if (flowAmp > 0.002) {
          const stream = 0.5 + 0.5 * Math.sin(q.seed * 3 - t * 3.4);
          r *= 1 - flowAmp * stream * stream;
        }

        let ox = shakeX;
        let oy = shakeY;
        if (driftAmp > 0.01) {
          ox += driftAmp * (Math.sin(t * 0.55 + q.seed * 3.7) + 0.5 * Math.sin(t * 1.3 + q.seed * 1.3));
          oy += driftAmp * (Math.cos(t * 0.62 + q.seed * 2.9) + 0.5 * Math.sin(t * 1.05 + q.seed * 5.1));
        }
        if (jitterAmp > 0.01) {
          ox += jitterAmp * Math.sin(t * 14 + q.seed * 9.3);
          oy += jitterAmp * Math.cos(t * 17 + q.seed * 6.1);
        }

        let screenX = center + x1 * r * perspective + ox;
        let screenY = center + y1 * r * perspective + oy;
        let alpha = 0.12 + depth * depth * 0.78;
        let dot = 0.6 + depth * 1.5;

        if (ringW > 0.004) {
          const ringAngle = (q.index / PARTICLE_COUNT) * TWO_PI + ringPhase + 0.05 * Math.sin(t * 1.3 + q.seed);
          const ringR = center * (0.58 + 0.13 * q.ringFrac) * (1 + 0.05 * Math.sin(t + q.seed * 1.7)) * ringBreath;
          screenX += (center + Math.cos(ringAngle) * ringR - screenX) * ringW;
          screenY += (center + Math.sin(ringAngle) * ringR - screenY) * ringW;
          alpha += (0.35 + q.tone * 0.5 - alpha) * ringW;
          dot += (0.75 + q.tone * 0.9 - dot) * ringW;
        }

        q.sx = screenX;
        q.sy = screenY;
        q.r = dot * dotScale;
        const alphaBucket = Math.min(ALPHA_BUCKETS - 1, Math.floor(alpha * alphaScale * ALPHA_BUCKETS));
        buckets[q.toneBucket * ALPHA_BUCKETS + alphaBucket]?.push(q);
      }

      ensurePalette(frame.weights.error);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);
      ctx.globalCompositeOperation = lookRef.current === 'glow' ? 'lighter' : 'source-over';
      buckets.forEach((bucket, b) => {
        if (bucket.length === 0) return;
        const alphaBucket = b % ALPHA_BUCKETS;
        ctx.globalAlpha = clamp01(((alphaBucket + 0.5) / ALPHA_BUCKETS) * p.alpha);
        ctx.fillStyle = toneStyles[(b - alphaBucket) / ALPHA_BUCKETS] ?? '#fff';
        ctx.beginPath();
        for (const q of bucket) {
          ctx.moveTo(q.sx + q.r, q.sy);
          ctx.arc(q.sx, q.sy, q.r, 0, TWO_PI);
        }
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    drawRef.current = draw;
    return () => { drawRef.current = null; };
  }, [size]);

  return (
    <div ref={hostRef} role="img" aria-label={label} data-state={state}
      style={{ width: size, height: size, opacity: state === 'disabled' ? 0.5 : 1, transition: 'opacity 0.4s ease' }}>
      <canvas ref={canvasRef} style={{ width: size, height: size, display: 'block', filter: look === 'glow' ? GLOW : SHADOW }} />
    </div>
  );
}
